import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { stringifyGraph } from '@/clients/http'
import { ProductModel } from '@/http/models/Product/Product'

interface Props {
  productId: string
  value: string
  onChange: (variationId: string) => void
}

/**
 * Resolves a product's variations (from the product details API — there is no variation list endpoint)
 * and lets the merchant pick one by name/SKU instead of hand-typing a raw MongoDB ObjectId.
 */
export const CampaignVariationPicker = ({ productId, value, onChange }: Props) => {
  const [variations, setVariations] = useState<{ _id: string; name: string; sku: string }[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!productId) {
      setVariations([])
      return
    }
    let cancelled = false
    setLoading(true)
    ProductModel.getById({
      params: { id: productId },
      query: { graph_enc: stringifyGraph({ _id: true, variations: { _id: true, name: true, sku: true } }) },
    })
      .then((response) => {
        if (cancelled) return
        if (response.success && response.data?.result) {
          setVariations(response.data.result.variations ?? [])
        } else {
          setVariations([])
          toast.error(response.error?.data?.message || 'Could not load variations')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [productId])

  if (!productId) {
    return <p className="flex h-8 items-center text-sm text-text-2">Select a product first</p>
  }

  return (
    <Select value={value} onValueChange={onChange} disabled={loading || variations.length === 0}>
      <SelectTrigger>
        <SelectValue placeholder={loading ? 'Loading variations…' : 'Select variation'} />
      </SelectTrigger>
      <SelectContent>
        {variations.map((v) => (
          <SelectItem key={v._id} value={v._id}>
            {v.name} {v.sku ? `(${v.sku})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
